import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import {
  CONTACT_SALES_URL,
  TILE_MODE,
  TILE_MODE_QUOTA,
} from '@explorer/constants';
import {getCurrentTileQuotas} from '@explorer/selectors/subscriptions';
import {notify} from '@explorer/actions/notifications';

export const OutOfTilesNotice = ({notify, tileMode, quotas = []}) => {
  const [shown, setShown] = useState({});
  const didMountRef = useRef(false);
  const {t} = useTranslation();

  const isBasemapMode = useMemo(
    () => tileMode === TILE_MODE.BASEMAPS,
    [tileMode]
  );

  const quotaType = TILE_MODE_QUOTA[tileMode];

  const lowQuotaSubs = quotas.filter(q => {
    const fraction = (q.remains / q.limit).toFixed(2);
    return !q.overage && fraction === '0.20';
  });

  const wasShown = useMemo(
    () =>
      lowQuotaSubs.every(qs => {
        return shown[`${qs.subscriptionId}-${quotaType}`];
      }),
    [lowQuotaSubs, shown, quotaType]
  );

  const doShow = lowQuotaSubs.length && !wasShown;

  const subNames = lowQuotaSubs.map(qs => qs.subscriptionName);
  const last = subNames.pop();
  const lowQuotaNames =
    subNames.length >= 1 ? `${subNames.join(', ')} and ${last}` : `${last}`;

  useEffect(() => {
    const message = (
      <Trans
        i18nKey="notifications.warningTileQuota"
        values={{
          quotaType,
          lowQuotaNames,
        }}
      />
    );

    const actions = [
      {
        label: t('notifications.promptContactSales'),
        key: 'out-of-tiles-contact-sales',
        href: CONTACT_SALES_URL,
        target: '_blank',
        rel: 'noreferrer noopener',
      },
    ];

    if (doShow) {
      if (didMountRef.current) {
        notify({
          key: 'out-of-tiles-notification',
          message,
          type: 'miniDialog',
          persist: true,
          actions,
        });
      }
      const shownIds = lowQuotaSubs.reduce((acc, sub) => {
        acc[`${sub.subscriptionId}-${quotaType}`] = true;
        return acc;
      }, {});
      setShown({...shown, ...shownIds});
    }
    if (!didMountRef.current) {
      didMountRef.current = true;
    }
  }, [
    notify,
    shown,
    doShow,
    lowQuotaSubs,
    lowQuotaNames,
    tileMode,
    quotaType,
    isBasemapMode,
    t,
  ]);

  // nothing to DOMify.
  return false;
};

OutOfTilesNotice.propTypes = {
  notify: PropTypes.func.isRequired,
  quotas: PropTypes.arrayOf(
    PropTypes.shape({
      limit: PropTypes.number,
      overage: PropTypes.bool,
      remains: PropTypes.number,
      subscriptionId: PropTypes.number,
      subscriptionName: PropTypes.string,
    })
  ),
  tileMode: PropTypes.string.isRequired,
};

export const mapStateToProps = state => ({
  quotas: getCurrentTileQuotas(state),
  tileMode: state.tiles.mode,
});

const mapDispatchToProps = {
  notify,
};

export default connect(mapStateToProps, mapDispatchToProps)(OutOfTilesNotice);
